import {BreakpointKey} from '@packages/systems/designer/styles';
import {BreakpointID} from '@packages/systems/style/types';

export const CONTAINER_NAME_PREFIX = 'wf-container-';

// Maps each breakpoint to all of its fallback breakpoints.
// For example, medium gets styles from medium and main, so {'medium' -> ['medium', 'main']}
const BREAKPOINT_FALLBACKS: Record<BreakpointID, readonly BreakpointID[]> = {
  tiny: ['tiny', 'small', 'medium', 'main'],
  small: ['small', 'medium', 'main'],
  medium: ['medium', 'main'],
  main: ['main'],
  large: ['large', 'main'],
  xl: ['xl', 'large', 'main'],
  xxl: ['xxl', 'xl', 'large', 'main'],
} as const;

/**
 * Takes breakpoint key and returns `${CONTAINER_NAME_PREFIX}${breakpointKey}`
 * @param  {BreakpointKey} breakpointKey          The breakpoint key (e.g. "medium" or "elements--medium")
 * @return {string}                               Container name with prefix (e.g. "wf-container-medium")
 */
export const prependContainerName = (breakpointKey: BreakpointKey): string => {
  return `${CONTAINER_NAME_PREFIX}${breakpointKey}`;
};

/**
 * Takes breakpoint id and returns full container name including all fallback breakpoints
 * @param  {BreakpointID} breakpointId            The breakpoint ID (e.g. "medium")
 * @return {string}                               Container name with prefix (e.g. "wf-container-medium wf-container-small wf-container-main")
 */
const buildContainerName = (breakpointId: BreakpointID): string => {
  return BREAKPOINT_FALLBACKS[breakpointId].map(prependContainerName).join(' ');
};

// Maps each breakpointId to its final container name
const BREAKPOINT_CONTAINER_MAP: Record<BreakpointID, string> = (
  Object.keys(BREAKPOINT_FALLBACKS) as BreakpointID[]
).reduce(
  (acc, breakpointId) => {
    acc[breakpointId] = buildContainerName(breakpointId);
    return acc;
  },
  {} as Record<BreakpointID, string>
);

// Given a breakpointId, returns the final container name for that breakpoint
export const getBreakpointContainerName = (
  breakpointId: BreakpointID
): string => {
  return BREAKPOINT_CONTAINER_MAP[breakpointId];
};
